import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, CreditCard, Wallet } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PlaidAccount {
  id: number;
  plaidItemId: number;
  businessId: number;
  accountId: string;
  name: string;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  balanceCurrent: string | null;
  balanceAvailable: string | null;
  isSelected: boolean;
}

export function AccountSelector({ businessId }: { businessId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = [`/api/businesses/${businessId}/plaid/linked-accounts`];

  const { data: accounts, isLoading } = useQuery<PlaidAccount[]>({
    queryKey,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/plaid/linked-accounts`);
      return res.json();
    },
    enabled: !!businessId,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ accountId, isSelected }: { accountId: number; isSelected: boolean }) => {
      const res = await apiRequest("PATCH", `/api/businesses/${businessId}/plaid/linked-accounts/${accountId}`, { isSelected });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!accounts || accounts.length === 0) {
    return null; // No Plaid accounts yet — nothing to show
  }

  const iconForType = (type: string | null) => {
    if (type === "depository") return <Landmark className="h-5 w-5 text-blue-600" />;
    if (type === "credit") return <CreditCard className="h-5 w-5 text-orange-600" />;
    return <Wallet className="h-5 w-5 text-gray-600" />;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Select Accounts to Import</CardTitle>
        <CardDescription>Choose which bank accounts to include when importing transactions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {accounts.map((acct) => (
          <div
            key={acct.id}
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
              acct.isSelected ? "bg-blue-50/50 border-blue-200" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex items-center gap-3">
              {iconForType(acct.type)}
              <div>
                <div className="font-medium text-sm">{acct.name}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {acct.mask && <span>****{acct.mask}</span>}
                  {acct.subtype && (
                    <Badge variant="outline" className="text-xs px-1.5 py-0">
                      {acct.subtype}
                    </Badge>
                  )}
                  {acct.balanceCurrent && (
                    <span>${parseFloat(acct.balanceCurrent).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </div>
            </div>
            <Switch
              checked={acct.isSelected}
              onCheckedChange={(checked) => toggleMutation.mutate({ accountId: acct.id, isSelected: checked })}
              disabled={toggleMutation.isPending}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
